// Firestore CRUD Hook for React
// Provides easy-to-use methods for database operations
// Generated by @firebase-setup agent

import { useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebaseConfig.js';
import { useAuth } from '../contexts/AuthContext.jsx';

export const useFirestore = (collectionName) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  /**
   * Create a new document
   * @param {object} data - Document data
   * @returns {Promise<{success: boolean, id?: string, error?: string}>}
   */
  const createDocument = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const docData = {
        ...data,
        userId: currentUser?.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, collectionName), docData);
      setLoading(false);
      return { success: true, id: docRef.id };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Read a single document by ID
   * @param {string} docId - Document ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  const readDocument = async (docId) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);

      setLoading(false);

      if (docSnap.exists()) {
        return {
          success: true,
          data: { id: docSnap.id, ...docSnap.data() }
        };
      } else {
        return { success: false, error: 'Document not found' };
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Read all documents with optional query constraints
   * @param {Array} queryConstraints - Optional Firestore query constraints
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const readAllDocuments = async (queryConstraints = []) => {
    try {
      setLoading(true);
      setError(null);

      const collectionRef = collection(db, collectionName);
      const q = query(collectionRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);

      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      setLoading(false);
      return { success: true, data: documents };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Read user's own documents
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const readUserDocuments = async () => {
    if (!currentUser) {
      return { success: false, error: 'User not authenticated' };
    }

    return readAllDocuments([
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    ]);
  };

  /**
   * Update a document
   * @param {string} docId - Document ID
   * @param {object} data - Data to update
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const updateDocument = async (docId, data) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });

      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Delete a document
   * @param {string} docId - Document ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const deleteDocument = async (docId) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);

      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  return {
    loading,
    error,
    createDocument,
    readDocument,
    readAllDocuments,
    readUserDocuments,
    updateDocument,
    deleteDocument
  };
};

export default useFirestore;
